import { useCallback, useEffect, useReducer } from 'react';
import reducer from './reducer';
import useMemoCompare from '../useMemoCompare';

const usePagination = (query, pageLimit) => {
  const INITIAL_STATE = {
    status: query ? 'loading' : 'idle',
    data: undefined,
    error: undefined,
    lastDocument: undefined,
    firstDocument: undefined,
    hasMorePage: false,
  };

  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  const queryCached = useMemoCompare(query, prevQuery => {
    // Use built-in Firestore isEqual method to determine if "equal"
    return prevQuery && query && query.isEqual(prevQuery);
  });

  useEffect(() => {
    if (!queryCached) {
      dispatch({ type: 'idle' });
      return;
    }
    dispatch({ type: 'loading' });
    const loadData = async () => {
      try {
        const data = await queryCached.limit(pageLimit + 1).get();
        const payload = {
          data: data.docs,
          pageLimit: pageLimit,
        };
        dispatch({ type: 'success', payload: payload });
      } catch (error) {
        //Temp: indexes error log
        dispatch({ type: 'error', payload: error });
      }
    };
    loadData();
  }, [queryCached, pageLimit]);

  const nextPage = useCallback(async () => {
    dispatch({ type: 'loading' });
    try {
      const data = await queryCached
        .startAfter(state.lastDocument)
        .limit(pageLimit + 1)
        .get();
      const payload = {
        data: data.docs,
        pageLimit: pageLimit,
      };
      dispatch({ type: 'nextPage', payload: payload });
    } catch (error) {
      //Temp: indexes error log
      dispatch({ type: 'error', payload: error });
    }
  }, [queryCached, state.lastDocument, pageLimit]);

  const prevPage = useCallback(async () => {
    dispatch({ type: 'loading' });
    try {
      const data = await queryCached
        .endBefore(state.firstDocument)
        .limitToLast(pageLimit + 1)
        .get();
      const payload = {
        data: data.docs,
        pageLimit: pageLimit,
      };
      dispatch({ type: 'prevPage', payload: payload });
    } catch (error) {
      //Temp: indexes error log
      dispatch({ type: 'error', payload: error });
    }
  }, [queryCached, state.firstDocument, pageLimit]);

  return {
    status: state.status,
    data: state.data,
    error: state.error,
    hasMorePage: state.hasMorePage,
    nextPage,
    prevPage,
  };
};

export default usePagination;
